<script setup lang="ts">
import type { Follower } from '@/types/profile';

const props = defineProps({
	follower: {
		type: Object as PropType<Follower>,
		required: true,
	},
	isLoading: {
		type: Boolean,
		default: false,
	},
	processingId: {
		type: Number as PropType<number | null>,
		default: null,
	},
	isAdminView: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits<{
	(e: 'onMessage', userId: number, userName: string): void;
	(e: 'onPromote', userId: number, userName: string): void;
	(e: 'onDemote', userId: number): void;
}>();

const { isTablet } = useWindowResize();

const followerId = computed(() => props.follower.id);
const isOwner = computed(() => props.follower.role === GROUP_ROLE.OWNER);

const orderStatus = computed(() => {
	switch (props.follower.role) {
		case GROUP_ROLE.ADMIN:
			return GROUP_ROLE_NAME.ADMIN;
		case GROUP_ROLE.MEMBER:
			return GROUP_ROLE_NAME.MEMBER;
		case GROUP_ROLE.OWNER:
			return GROUP_ROLE_NAME.OWNER;
		default:
			return GROUP_ROLE_NAME.FOLLOWER;
	}
});

const orderStatusColor = computed(() => {
	switch (props.follower.role) {
		case GROUP_ROLE.ADMIN:
			return CHIP_COLOR.PRIMARY;
		case GROUP_ROLE.MEMBER:
			return CHIP_COLOR.GRAY;
		case GROUP_ROLE.OWNER:
			return CHIP_COLOR.SUCCESS;
		default:
			return CHIP_COLOR.GRAY;
	}
});

const actionText = computed(() => {
	if (props.follower.role === GROUP_ROLE.MEMBER || props.follower.role === GROUP_ROLE.FOLLOWER) {
		return 'Add Co-organizer';
	} else if (props.follower.role === GROUP_ROLE.ADMIN) {
		return 'Remove Co-organizer';
	}
	return 'Organizer';
});

const handleMessage = () => {
	emit('onMessage', props.follower.id, props.follower.nickname);
};

const handlePromote = () => {
	emit('onPromote', props.follower.id, props.follower.nickname);
};

const handleDemote = () => {
	emit('onDemote', props.follower.id);
};

const handleAction = () => {
	if (props.follower.role === GROUP_ROLE.MEMBER || props.follower.role === GROUP_ROLE.FOLLOWER) {
		handlePromote();
	} else if (props.follower.role === GROUP_ROLE.ADMIN) {
		handleDemote();
	}
};
</script>

<template>
  <tr class="cohost-item text-sm text-medium">
    <template v-if="!isTablet">
      <td>
        <AvatarLink
          class="avatar-link"
          :image-url="follower.avatar"
          :alt="follower.nickname"
          :size="AvatarSizes[2]"
          :user-id="follower.id"
        >
          <template #content>
            <div :class="['cohost-info-container', { '--is-admin-view': isAdminView }]">
              <p class="cohost-name">
                {{ follower.nickname || '' }}
              </p>
            </div>
          </template>
        </AvatarLink>
      </td>
      <td class="text-center">
        <BaseChip
          class="text-xs"
          :color="orderStatusColor"
        >
          <p>{{ orderStatus }}</p>
        </BaseChip>
      </td>
      <template v-if="!isAdminView">
        <td class="text-center">
          <BaseButton
            v-if="!isOwner"
            variant="subtle"
            color="gray"
            size="sm"
            icon-only
            :disabled="isLoading"
            @click="handleAction"
          >
            <template #rightIcon>
              <UsersXIcon v-if="props.follower.role === GROUP_ROLE.ADMIN" :color="ICON_COLOR.ERROR" />
              <UsersPlusIcon v-else :color="ICON_COLOR.PRIMARY" />
            </template>
            <span
              :class="['action-text', {
                '--is-follower': props.follower.role === GROUP_ROLE.FOLLOWER || props.follower.role === GROUP_ROLE.MEMBER,
                '--is-admin': props.follower.role === GROUP_ROLE.ADMIN,
              }]"
            >
              {{ actionText }}
            </span>
          </BaseButton>
        </td>
        <td class="text-center">
          <BaseButton
            v-if="!isOwner"
            variant="subtle"
            color="gray"
            size="sm"
            icon-only
            :disabled="isLoading"
            @click="handleMessage"
          >
            <MailIcon />
          </BaseButton>
        </td>
      </template>
    </template>

    <template v-else>
      <div class="cohost-mobile-container">
        <div :class="['cohost-info-container', { '--is-admin-view': isAdminView }]">
          <AvatarLink
            class="cohost-image"
            :image-url="follower.avatar"
            :alt="follower.nickname"
            :size="AvatarSizes[2]"
            :user-id="follower.id"
          />
          <RouterLink
            class="cohost-name w-full"
            :to="{
              name: ROUTE.USER.NAME,
              params: {
                id: follower.id
              }
            }"
          >
            <p class="text-left">
              {{ follower.nickname }}
            </p>
          </RouterLink>
          <BaseChip
            class="text-xs"
            :color="orderStatusColor"
          >
            <p>{{ orderStatus }}</p>
          </BaseChip>
          <div v-if="!isAdminView" class="cohost-action">
            <BaseButton
              v-if="!isOwner"
              :id="`cohost-menu-activator-${followerId}`"
              variant="outlined"
              color="gray"
              size="sm"
              icon-only
            >
              <DotsHorizontalIcon />
            </BaseButton>
            <VMenu
              v-if="!isOwner"
              :activator="`#cohost-menu-activator-${followerId}`"
              location="bottom"
              width="200"
            >
              <VList class="menu-list text-sm text-medium">
                <VListItem
                  min-height="42"
                  class="menu-item"
                  :disabled="isLoading"
                  @click="handleMessage"
                >
                  <template #prepend>
                    <MailIcon />
                  </template>
                  <span>Message</span>
                </VListItem>
                <VListItem
                  min-height="42"
                  class="menu-item"
                  :disabled="isLoading"
                  @click="handleAction"
                >
                  <template #prepend>
                    <UsersXIcon v-if="props.follower.role === GROUP_ROLE.ADMIN" :color="ICON_COLOR.ERROR" />
                    <UsersPlusIcon v-else :color="ICON_COLOR.PRIMARY" />
                  </template>
                  <span
                    :class="['action-text', {
                      '--is-follower': props.follower.role === GROUP_ROLE.FOLLOWER || props.follower.role === GROUP_ROLE.MEMBER,
                      '--is-admin': props.follower.role === GROUP_ROLE.ADMIN,
                    }]"
                  >
                    {{ actionText }}
                  </span>
                </VListItem>
              </VList>
            </VMenu>
          </div>
        </div>
      </div>
    </template>
  </tr>
</template>

<style scoped lang="scss">
.cohost-item {
  .avatar-link p {
    color: colors-get(gray, 900);
  }

  .cohost-info-container {
    display: flex;
    flex-direction: column;

    @include media-query-max(tablet) {
      display: grid;
      grid-template-columns: rem(40) auto auto;
      grid-template-rows: 1fr 1fr;
      column-gap: spacings-get(3);
      row-gap: spacings-get(1);

      &.--is-admin-view {
        grid-template-columns: rem(40) auto;

        .cohost-image {
          grid-area: 1 / 1 / 3 / 2;
        }

        .cohost-name {
          width: fit-content;
          grid-area: 1 / 2 / 2 / 3;
        }

        .cohost-status {
          grid-area: 2 / 2 / 2 / 3;
          width: fit-content;
        }
      }

      .cohost-image {
        grid-area: 1 / 1 / 3 / 2;
      }

      .cohost-name {
        width: fit-content;
        grid-area: 1 / 2 / 1 / 3;
      }

      .cohost-status {
        grid-area: 2 / 2 / 2 / 3;
        width: fit-content;
      }

      .cohost-action {
        grid-area: 1 / 3 / 3 / 4;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        column-gap: spacings-get(2);
      }
    }

    .cohost-name {
      @include fonts-get(medium, 'text-sm');
      color: colors-get(gray, 900);
      text-decoration: none;
    }
  }

  .cohost-mobile-container {
    display: flex;
    flex-direction: column;
    row-gap: spacings-get(3);
    padding: spacings-get(4) 0;
    border-bottom: 1px solid colors-get(gray, 200);
  }
}

.action-text {
  &.--is-follower {
    color: colors-get(primary, 700);
  }
  &.--is-admin {
    color: colors-get(error, 700) !important;
  }
}

.menu-list {
  :deep(.v-list-item) {
    padding: 0 spacings-get(4) !important;
  }

  .menu-item {
    gap: spacings-get(2);
    box-sizing: border-box;
  }
}
</style>

